import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { forwardRef, useState, useImperativeHandle } from "react";
import CancelIcon from "../../../assets/icons/cross-bold-icon.png";
import GlobalButton from "../../../components/button";
import Label from "../../../components/label";
import TextInput from "../../../components/textInput";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { addTarrifValidation } from "../../../utils/Validation";
import {
  createBroadBand,
  updateBroadBand,
} from "../../../services/BroadbandApi";
import AppLoader from "./../../../components/loader/index";
import { handleApiCall } from "../../../utils/helper";
import CustomCheckBox from "../../../components/checkbox";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": { padding: theme.spacing(2) },
  "& .MuiPaper-root": {
    width: "600px",
    [theme.breakpoints.down("sm")]: { margin: 12 },
  },
}));

const AddEditBroadBand = forwardRef(({}, ref) => {
  const [open, setOpen] = useState(false);
  const [dialogData, setDialogData] = useState(null);
  const [otherProducts, setOtherProducts] = useState({});
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const [editKey, setEditKey] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [inputValues, setInputValues] = useState({
    type: "",
    value: "",
    description: "",
    salePrice: "",
    buyPrice: "",
    highPrice: "",
    length12: "",
    length18: "",
    length24: "",
    length36: "",
    isUploadSpeed: false,
    isDownloadSpeed: false,
    includeDateFairUsage:false
  });
  const [inputError, setInputError] = useState({
    type: false,
    value: false,
    description: false,
    salePrice: false,
    buyPrice: false,
    highPrice: false,
    length12: false,
    length18: false,
    length24: false,
    length36: false,
  });

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setOpen(true);
      setDialogData(params);

      if (params.type === "edit") {
        const { data } = params;
        setOtherProducts(data.other);
        setInputValues({
          type: data?.type,
          value: data?.value,
          description: data?.description,
          salePrice: data?.salePrice,
          buyPrice: data?.buyPrice,
          highPrice: data?.highPrice,
          length12: data?.length12,
          length18: data?.length18,
          length24: data?.length24,
          length36: data?.length36,
          isUploadSpeed: data?.isUploadSpeed,
          isDownloadSpeed: data?.isDownloadSpeed,
          includeDateFairUsage: data?.includeDateFairUsage,
        });
      }
    },
    disable() {
      handleClose();
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setInputValues({});
    setOtherProducts({});
  };

  const handleChangeInputValue = (event) => {
    const { name, value } = event.target;
    setInputError((prev) => ({ ...prev, [name]: false }));
    setInputValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddOrUpdateProduct = () => {
    if (newKey.trim() && newValue.trim()) {
      setOtherProducts((prev) => {
        const updatedProducts = { ...prev };

        if (editKey !== null && editKey !== newKey) {
          delete updatedProducts[editKey];
        }

        updatedProducts[newKey] = newValue;
        return updatedProducts;
      });

      setNewKey("");
      setNewValue("");
      setEditKey(null);
    }
  };

  const handleEdit = (key, value) => {
    setNewKey(key);
    setNewValue(value);
    setEditKey(key);
  };

  const handleDelete = (key) => {
    setOtherProducts((prev) => {
      const updatedProducts = { ...prev };
      delete updatedProducts[key];
      return updatedProducts;
    });
  };

  const handleAddEditTarrif = async () => {
    const validate = addTarrifValidation(inputValues, setInputError);
    if (validate) {
      return;
    }

    let payload = {
      type: dialogData?.value,
      value: inputValues?.value,
      description: inputValues?.description,
      buyPrice: inputValues.buyPrice,
      salePrice: inputValues?.salePrice,
      highPrice: inputValues.highPrice,
      length12: inputValues.length12,
      length18: inputValues.length18,
      length24: inputValues.length24,
      length36: inputValues.length36,
      other: otherProducts,
      isAdditional: false,
      isUploadSpeed: inputValues?.isUploadSpeed,
      isDownloadSpeed: inputValues?.isDownloadSpeed,
      includeDateFairUsage: inputValues?.includeDateFairUsage,
    };

    if (dialogData?.type === "add") {
      handleApiCall(
        "",
        createBroadBand,
        payload,
        setIsLoading,
        handleClose,
        dialogData.getData
      );
    } else {
      handleApiCall(
        dialogData?.data?._id,
        updateBroadBand,
        payload,
        setIsLoading,
        handleClose,
        dialogData.getData
      );
    }
  };

  return (
    <BootstrapDialog onClose={handleClose} open={open}>
      <AppLoader isVisible={isLoading} />
      <Stack
        p={2}
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Typography
          variant="h1"
          fontSize={"1.5rem"}
          fontWeight={500}
          color={"secondary.dark"}
        >
          {dialogData?.type === "add" ? "Add Tariff" : "Edit Tariff"}
        </Typography>
        <IconButton onClick={handleClose}>
          <img src={CancelIcon} alt="cancel-icon" width={25} />
        </IconButton>
      </Stack>
      <DialogContent dividers>
        <Box margin={"auto"}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Label title={"Tariff Name"} required htmlFor={"customer-name"} />
              <TextInput
                id={"customer-name"}
                type={"text"}
                required
                error={inputError.value}
                helperText={inputError.value && "Tariff name is required"}
                defaultStyle={true}
                placeholder={"Enter tarrif name"}
                value={inputValues.value}
                name={"value"}
                onChange={handleChangeInputValue}
              />
            </Grid>
            <Grid item xs={12}>
              <Label
                title={"Short Description"}
                required
                htmlFor={"short-description"}
              />
              <TextInput
                id={"short-description"}
                type={"text"}
                required
                error={inputError.description}
                helperText={inputError.description && "Description is required"}
                defaultStyle={true}
                placeholder={"Enter tariff description"}
                value={inputValues.description}
                name={"description"}
                onChange={handleChangeInputValue}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Label title={"Buy Price"} required htmlFor={"buy-price"} />
              <TextInput
                id={"buy-price"}
                type={"number"}
                required
                error={inputError.buyPrice}
                helperText={inputError.buyPrice && "Buy price is required"}
                defaultStyle={true}
                placeholder={"Enter buy price"}
                value={inputValues.buyPrice}
                name={"buyPrice"}
                onChange={handleChangeInputValue}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Label title={"Sale Price"} required htmlFor={"sale-price"} />
              <TextInput
                id={"sale-price"}
                type={"number"}
                required
                error={inputError.salePrice}
                helperText={inputError.salePrice && "Sale price is required"}
                defaultStyle={true}
                placeholder={"Enter sale price"}
                value={inputValues.salePrice}
                name={"salePrice"}
                onChange={handleChangeInputValue}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Label title={"High Price"} required htmlFor={"high-price"} />
              <TextInput
                id={"high-price"}
                type={"number"}
                required
                error={inputError.highPrice}
                helperText={inputError.highPrice && "High price is required"}
                defaultStyle={true}
                placeholder={"Enter high price"}
                value={inputValues.highPrice}
                name={"highPrice"}
                onChange={handleChangeInputValue}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Label
                title={"Price ( Length 12 )"}
                required
                htmlFor={"length12-price"}
              />
              <TextInput
                id={"length12-price"}
                type={"number"}
                required
                error={inputError.length12}
                helperText={
                  inputError.length12 &&
                  "Price for Contract length 12 is required"
                }
                defaultStyle={true}
                placeholder={"Enter lenght 12 price"}
                value={inputValues.length12}
                name={"length12"}
                onChange={handleChangeInputValue}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Label
                title={"Price ( Length 18 )"}
                required
                htmlFor={"length18-price"}
              />
              <TextInput
                id={"length18-price"}
                type={"number"}
                required
                error={inputError.length18}
                helperText={
                  inputError.length18 &&
                  "Price for Contract length 18 is required"
                }
                defaultStyle={true}
                placeholder={"Enter length 18 price"}
                value={inputValues.length18}
                name={"length18"}
                onChange={handleChangeInputValue}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Label
                title={"Price ( Length 24 )"}
                required
                htmlFor={"length24-price"}
              />
              <TextInput
                id={"length24-price"}
                type={"number"}
                required
                error={inputError.length24}
                helperText={
                  inputError.length24 &&
                  "Price for Contract length 24 is required"
                }
                defaultStyle={true}
                placeholder={"Enter length 24 price"}
                value={inputValues.length24}
                name={"length24"}
                onChange={handleChangeInputValue}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Label
                title={"Price ( Length 36 )"}
                required
                htmlFor={"length36-price"}
              />
              <TextInput
                id={"length36-price"}
                type={"number"}
                required
                error={inputError.length36}
                helperText={
                  inputError.length36 &&
                  "Price for Contract length 36 is required"
                }
                defaultStyle={true}
                placeholder={"Enter length 36 price"}
                value={inputValues.length36}
                name={"length36"}
                onChange={handleChangeInputValue}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack gap={1} direction={"row"} alignItems={"center"}>
                <CustomCheckBox
                  isChecked={inputValues.isUploadSpeed}
                  handleChecked={() =>
                    setInputValues((pre) => ({
                      ...pre,
                      isUploadSpeed: !pre.isUploadSpeed,
                    }))
                  }
                />
                <Typography>Include Upload Speed</Typography>
              </Stack>
              <Stack gap={1} direction={"row"} alignItems={"center"}>
                <CustomCheckBox
                  isChecked={inputValues.isDownloadSpeed}
                  handleChecked={() =>
                    setInputValues((pre) => ({
                      ...pre,
                      isDownloadSpeed: !pre.isDownloadSpeed,
                    }))
                  }
                />
                <Typography>Include Download Speed</Typography>
              </Stack>
              <Stack gap={1} direction={"row"} alignItems={"center"}>
                <CustomCheckBox
                  isChecked={inputValues.includeDateFairUsage}
                  handleChecked={() =>
                    setInputValues((pre) => ({
                      ...pre,
                      includeDateFairUsage: !pre.includeDateFairUsage,
                    }))
                  }
                />
                <Typography>Include Data Fair Usage</Typography>
              </Stack>
            </Grid>
          </Grid>

          <Typography
            mt={1.5}
            variant="h6"
            fontWeight={500}
            gutterBottom
            borderBottom={"2px solid blue"}
            display={"inline-block"}
          >
            Add Others :
          </Typography>

          <Grid container spacing={2} alignItems={"flex-end"} mb={2}>
            <Grid item xs={5}>
              <Label title="Title" required htmlFor={"key-title"} />
              <TextInput
                id={"key-title"}
                type="text"
                required
                defaultStyle={true}
                placeholder="Enter title"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
              />
            </Grid>

            <Grid item xs={5}>
              <Label title="Value" required htmlFor={"value"} />
              <TextInput
                id={"value"}
                type="text"
                required
                defaultStyle={true}
                placeholder="Enter value"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
              />
            </Grid>

            <Grid item xs={2} display="flex" alignItems="center">
              <GlobalButton
                variant="custom_gradient"
                title={editKey !== null ? "Update" : "Add"}
                handleClickBtn={handleAddOrUpdateProduct}
                style={{
                  height: 45,
                  borderRadius: 2,
                }}
              />
            </Grid>
          </Grid>

          {Object.entries(otherProducts).map(([key, value], index) => (
            <Box item xs={12} key={index} display="flex" alignItems="center">
              <Stack
                flex={1}
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                border={"1px solid grey"}
                borderRadius={1}
                overflow={"hidden"}
              >
                <Box flex={1} p={1} bgcolor={"#ececec"}>
                  <Typography variant="body2" fontWeight={500}>
                    {key}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box flex={1} p={1}>
                  <Typography variant="body2">{value}</Typography>
                </Box>
                <Divider orientation="vertical" flexItem />

                <IconButton
                  onClick={() => handleEdit(key, value)}
                  color="primary"
                  sx={{ p: "5px" }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <Divider orientation="vertical" flexItem />

                <IconButton
                  onClick={() => handleDelete(key)}
                  sx={{ p: "5px" }}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <GlobalButton
          variant={"custom_gradient"}
          handleClickBtn={handleClose}
          title={"Cancel"}
          style={{
            width: 100,
            background: "red !important",
          }}
        />
        <GlobalButton
          variant={"custom_gradient"}
          title={dialogData?.type === "add" ? "Add" : "Update"}
          style={{ width: 100 }}
          handleClickBtn={handleAddEditTarrif}
        />
      </DialogActions>
    </BootstrapDialog>
  );
});

export default AddEditBroadBand;
